import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TablePagination,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import PDFViewer from '../shared/PDFViewer';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';

interface MovimientoBancario {
  nroFormulario?: number;
  nroTipoFormulario?: string;
  nroSuc?: number;
  fechaEmision?: string;
  codigoCaja?: number;
  usuario?: string;
  nombre?: string;
  descripcion?: string;
  importeTotal?: number;
  letra?: string;
  nroComprobante?: number;
  tipoPago?: string;
  ingresoEgreso?: string;
  detalle?: string;
  // Campos adicionales que pueden venir
  medioDeCobro?: string;
  medioCobro?: string;
  mcp?: string;
  medios?: Array<{
    ingresoEgreso?: string;
    [key: string]: any;
  }>;
  fecha?: string;
  comprobante?: string;
  tipo?: string;
  tipoComprobante?: string;
  cartera?: string;
  debe?: number;
  haber?: number;
  saldo?: number;
  [key: string]: any;
}

const MovimientosBancarios: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // 0: TODOS, 1: DEPOSITOS, 2: EXTRACCIONES
  const [currentTab, setCurrentTab] = useState(0);
  const [movimientos, setMovimientos] = useState<MovimientoBancario[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState<string>('fechaEmision');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  const [fechaDesde, setFechaDesde] = useState<string>(() => {
    return getFirstDayOfMonth().toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState<string>(() => {
    return getLastDayOfMonth().toISOString().split('T')[0];
  });
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedMovimientoId, setSelectedMovimientoId] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);

  const cargarMovimientos = async () => {
    setLoading(true);
    try {
      // Para Movimientos Bancarios necesitamos traer TODAS las páginas del backend
      // y luego separar en Depósitos / Extracciones según nroTipoFormulario (55 / 56).
      const pageSize = 50; // Backend: máximo 50
      const baseParams: Record<string, string | number | undefined> = {
        // El endpoint de movimientos bancarios declara estos parámetros como date-time.
        // En Swagger se los ve en formato ISO (YYYY-MM-DDTHH:mm:ss), por lo que
        // enviamos directamente la fecha del input (YYYY-MM-DD) que el backend
        // debería interpretar correctamente como fecha.
        FechaDesde: fechaDesde,
        FechaHasta: fechaHasta,
        PageSize: pageSize,
      };

      let allItems: MovimientoBancario[] = [];
      let apiPage = 1;

      // Bucle para obtener todas las páginas disponibles
      // Se detiene cuando una página devuelve menos registros que pageSize o queda vacía.
      // No se usa TipoMovimiento: el filtrado se hace en cliente por nroTipoFormulario.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const params = { ...baseParams, PageNumber: apiPage };
        const response = await api.get<any>('/api/TesoreriaCaja/movimientos-bancarios', { params });

        const data = response.data;
        let itemsPage: MovimientoBancario[] = [];

        if (Array.isArray(data)) {
          itemsPage = data;
        } else if (data && Array.isArray(data.items)) {
          itemsPage = data.items || [];
        } else if (data && data.data && Array.isArray(data.data)) {
          itemsPage = data.data || [];
        }

        if (!itemsPage.length) {
          break;
        }

        allItems = [...allItems, ...itemsPage];

        if (itemsPage.length < pageSize) {
          break;
        }

        apiPage += 1;
      }

      setMovimientos(allItems);
    } catch (error: any) {
      console.error('Error al cargar movimientos bancarios:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Error al cargar los movimientos bancarios',
        { variant: 'error' }
      );
      setMovimientos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarMovimientos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaDesde, fechaHasta]);

  const handleNuevoMovimiento = () => {
    // Se reutiliza el mismo formulario de "NuevoMovimientoCaja"
    // pero indicando que el origen es "bancario" para que utilice
    // tipoMovimiento 55 (Depósito) y 56 (Extracción).
    navigate('/tesoreria/movimientos-varios/nuevo', {
      state: {
        origen: 'bancario',
      },
    });
  };

  // Filtro por pestaña (Depósitos / Extracciones / Todos) y búsqueda
  const movimientosConFiltros = useMemo(() => {
    // Primero filtramos por tipo según la pestaña seleccionada
    let data = movimientos;
    if (currentTab === 1) {
      // Depósitos: nroTipoFormulario === "55"
      data = data.filter(
        (m) => String((m.nroTipoFormulario || '')).trim() === '55'
      );
    } else if (currentTab === 2) {
      // Extracciones: nroTipoFormulario === "56"
      data = data.filter(
        (m) => String((m.nroTipoFormulario || '')).trim() === '56'
      );
    }

    if (!searchTerm.trim()) return data;
    const term = searchTerm.trim().toLowerCase();
    return data.filter((m) => {
      const nombre = (m.nombre || '').toLowerCase();
      const descripcion = (m.descripcion || '').toLowerCase();
      const nroComprobante = String(m.nroComprobante ?? '').toLowerCase();
      const nroFormulario = String(m.nroFormulario ?? '').toLowerCase();
      return (
        nombre.includes(term) ||
        descripcion.includes(term) ||
        nroComprobante.includes(term) ||
        nroFormulario.includes(term)
      );
    });
  }, [movimientos, searchTerm, currentTab]);

  // Los datos ya vienen filtrados por tab (nroTipoFormulario) y búsqueda
  const movimientosFiltrados = movimientosConFiltros;

  // Totales por pestaña (sin tener en cuenta el texto de búsqueda)
  const totalTodos = useMemo(
    () => movimientos.length,
    [movimientos]
  );

  const totalDepositos = useMemo(
    () =>
      movimientos.filter(
        (m) => String((m.nroTipoFormulario || '')).trim() === '55'
      ).length,
    [movimientos]
  );

  const totalExtracciones = useMemo(
    () =>
      movimientos.filter(
        (m) => String((m.nroTipoFormulario || '')).trim() === '56'
      ).length,
    [movimientos]
  );

  // Ordenar movimientos
  const movimientosOrdenados = useMemo(() => {
    const sorted = [...movimientosFiltrados];
    sorted.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === 'fechaEmision' || sortField === 'fecha') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue || '').toLowerCase();
      } else if (typeof aValue === 'number') {
        // Mantener como número para ordenamiento numérico
      } else {
        aValue = aValue || 0;
        bValue = bValue || 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [movimientosFiltrados, sortField, sortDirection]);

  // Preparar movimientos con los campos necesarios
  const movimientosConSaldo = useMemo(() => {
    return movimientosOrdenados.map(movimiento => {
      return {
        ...movimiento,
        // Mantener importeTotal para mostrar en la columna Importe
      };
    });
  }, [movimientosOrdenados]);

  // Paginación en cliente sobre los datos ya filtrados y ordenados
  const movimientosPaginados = useMemo(() => {
    const start = page * rowsPerPage;
    return movimientosConSaldo.slice(start, start + rowsPerPage);
  }, [movimientosConSaldo, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPage(0); // Resetear a la primera página al cambiar de tab
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const formatComprobante = (m: MovimientoBancario) => {
    const { letra, nroSuc, nroComprobante } = m;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }
    const formatPart = (value: number | string | undefined | null, length: number) => {
      if (value === null || value === undefined) return ''.padStart(length, '0');
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) return numeric.toString().padStart(length, '0');
      return String(value).padStart(length, '0');
    };
    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = (letra || '').trim().toUpperCase();
    return letraNormalizada ? `${letraNormalizada}-${sucursal}-${comprobante}` : '-';
  };

  const handleImprimirPDF = async (id: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        enqueueSnackbar('No hay token de autenticación', { variant: 'error' });
        return;
      }
      const response = await api.get(
        `/api/TesoreriaCaja/movimientos-bancarios/${id}/pdf`,
        { responseType: 'blob' }
      );
      const cd = (response.headers as any)?.['content-disposition'] as string | undefined;
      const backendFileName = getFileNameFromContentDisposition(cd);
      setPdfFileName(backendFileName);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfUrl(url);
      setSelectedMovimientoId(id);
    } catch (error) {
      enqueueSnackbar('Error al generar el PDF del movimiento bancario', { variant: 'error' });
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedMovimientoId(null);
    setPdfFileName(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Movimientos Bancarios
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNuevoMovimiento}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
              }}
            >
              Nuevo Movimiento
            </Button>
          </Stack>

          {/* Campo de búsqueda y filtros */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por número, descripción, nombre"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={currentTab} onChange={handleTabChange}>
              <Tab 
                label={currentTab === 0 ? `TODOS ${totalTodos}` : 'TODOS'}
                sx={{
                  textTransform: 'none',
                  fontWeight: currentTab === 0 ? 600 : 400,
                  color: currentTab === 0 ? '#1976d2' : 'inherit',
                  fontSize: '0.875rem',
                }}
              />
              <Tab 
                label={currentTab === 1 ? `DEPOSITOS ${totalDepositos}` : 'DEPOSITOS'}
                icon={<ArrowUpwardIcon sx={{ color: '#4caf50', fontSize: '1.2rem' }} />}
                iconPosition="start"
                sx={{
                  textTransform: 'none',
                  fontWeight: currentTab === 1 ? 600 : 400,
                  color: currentTab === 1 ? '#1976d2' : 'inherit',
                  fontSize: '0.875rem',
                }}
              />
              <Tab 
                label={currentTab === 2 ? `EXTRACCIONES ${totalExtracciones}` : 'EXTRACCIONES'}
                icon={<ArrowDownwardIcon sx={{ color: '#f44336', fontSize: '1.2rem' }} />}
                iconPosition="start"
                sx={{
                  textTransform: 'none',
                  fontWeight: currentTab === 2 ? 600 : 400,
                  color: currentTab === 2 ? '#1976d2' : 'inherit',
                  fontSize: '0.875rem',
                }}
              />
            </Tabs>
          </Box>

          {/* Tabla */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Nro. Mov. Interno</TableCell>
                      <TableCell 
                        sx={{ fontWeight: 600, cursor: 'pointer', minWidth: 100 }}
                        onClick={() => handleSort('fechaEmision')}
                      >
                        Fecha {sortField === 'fechaEmision' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 110 }}>Comprobante</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Motivo</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Importe</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {movimientosPaginados.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No hay movimientos para mostrar
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      movimientosPaginados.map((movimiento, index) => {
                        const nroTipoFormulario = String((movimiento.nroTipoFormulario || '').trim());
                        const esEgreso = nroTipoFormulario === '56';
                        const esIngreso = nroTipoFormulario === '55';
                        
                        const nombre = movimiento.nombre ? movimiento.nombre.trim() : '-';
                        const fecha = movimiento.fechaEmision || '';
                        
                        let tipo = '-';
                        const tipoForm = String((movimiento.nroTipoFormulario || '').trim());
                        if (tipoForm === '55') {
                          tipo = 'Depósito';
                        } else if (tipoForm === '56') {
                          tipo = 'Extracción';
                        }
                        
                        const importe = movimiento.importeTotal || 0;
                        
                        const backgroundColor = esIngreso 
                          ? '#e8f5e9'
                          : esEgreso
                          ? '#ffebee'
                          : '#fff';
                        
                        return (
                          <TableRow
                            key={movimiento.nroFormulario ?? index}
                            sx={{
                              backgroundColor,
                              '&:hover': { 
                                backgroundColor: esIngreso 
                                  ? '#c8e6c9'
                                  : esEgreso
                                  ? '#ffcdd2'
                                  : '#f9f9f9'
                              }
                            }}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>{movimiento.nroFormulario ?? '-'}</TableCell>
                            <TableCell>{fecha ? formatDate(fecha) : '-'}</TableCell>
                            <TableCell>{formatComprobante(movimiento)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {esIngreso && (
                                  <ArrowUpwardIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                                )}
                                {esEgreso && (
                                  <ArrowDownwardIcon sx={{ color: '#f44336', fontSize: 16 }} />
                                )}
                                {nombre}
                              </Box>
                            </TableCell>
                            <TableCell>{tipo}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                              {importe > 0 ? formatCurrency(importe) : '-'}
                            </TableCell>
                            <TableCell>{movimiento.usuario ?? '-'}</TableCell>
                            <TableCell align="center">
                              {movimiento.nroFormulario != null && (
                                <Tooltip title="Imprimir PDF">
                                  <IconButton
                                    onClick={() => handleImprimirPDF(movimiento.nroFormulario!)}
                                    size="small"
                                    sx={{
                                      bgcolor: '#2196f3',
                                      color: '#fff',
                                      borderRadius: 1.5,
                                      '&:hover': { bgcolor: '#1976d2' }
                                    }}
                                  >
                                    <PrintIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={movimientosConSaldo.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </>
          )}
        </CardContent>
      </Card>

      {pdfUrl && selectedMovimientoId != null && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Movimiento ${selectedMovimientoId}`}
          documentId={`${selectedMovimientoId}`}
          downloadFileName={pdfFileName ?? undefined}
        />
      )}
    </Box>
  );
};

export default MovimientosBancarios;

